import { Tabs } from 'expo-router';
import { useSelector, useDispatch } from 'react-redux';
import { View, Text, ActivityIndicator, Platform, StyleSheet } from 'react-native';
import { useEffect, useRef } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Feather from '@expo/vector-icons/Feather';
import { Colors, Paprika } from '../../src/styles/theme';
import { RootState, AppDispatch, store, persistor } from '../../src/redux/store';
import { flushPendingMealPlanSync } from '../../src/redux/mealPlanSyncMiddleware';
import { fullSync } from '../../src/redux/inventorySlice';
import { loadPantryLearning } from '../../src/redux/pantryLearningSlice';
import { downloadMealPlanFromCloud, rehydrateMissingRecipes, pushAllWeeksToCloud } from '../../src/redux/mealPlanSlice';
import { downloadGroceryListFromCloud } from '../../src/redux/groceryListSlice';
import { downloadFeedbackFromCloud } from '../../src/redux/feedbackSlice';
import { downloadRecipeMetadataFromCloud } from '../../src/redux/recipeMetadataSlice';
import { downloadCookLogFromCloud } from '../../src/redux/cookLogSlice';
import { downloadUserPreferencesFromCloud } from '../../src/redux/userPreferencesSlice';
import { loadHouseholdId } from '../../src/redux/authSlice';
import { NavigationRestorer } from '../../src/components/NavigationRestorer';
import { DesktopSidebar } from '../../src/components/DesktopSidebar';
import { useGroceryListSync } from '../../src/hooks/useGroceryListSync';
import { useHouseholdLiveSync } from '../../src/hooks/useHouseholdLiveSync';
import { useTranslation } from 'react-i18next';
import { isDesktopWeb } from '../../src/utils/platformDetection';
import GroceryToast from '../../src/components/GroceryToast';
import ChefToast from '../../src/components/ChefToast';
import { VISIBLE_TABS, HIDDEN_ROUTES, TAB_BACK_BEHAVIOR } from '../../src/navigation/tabConfig';

// Module-level guard survives component remounts (e.g. from navigation redirects)
let hasInitialSynced = false;

/**
 * Resolve once the mealPlan slice has read its own disk copy.
 *
 * mealPlan is a nested IndexedDB persist, so its REHYDRATE lands independently
 * of the root one that `shouldSync` waits on. Anything that reasons about
 * "which weeks does this device have" has to wait for this or it reads {}.
 *
 * Resolves false on timeout rather than hanging: a profile whose IndexedDB
 * never opens still gets the rest of the sync.
 */
const whenLocalMealPlanReady = (timeoutMs = 15000): Promise<boolean> =>
  new Promise((resolve) => {
    if (store.getState().mealPlan.localHydrated) return resolve(true);
    const timer = setTimeout(() => {
      unsubscribe();
      console.warn('Meal plan disk copy did not arrive in time; skipping adoption');
      resolve(false);
    }, timeoutMs);
    const unsubscribe = store.subscribe(() => {
      if (store.getState().mealPlan.localHydrated) {
        clearTimeout(timer);
        unsubscribe();
        resolve(true);
      }
    });
  });

const styles = StyleSheet.create({
  tabLabel: {
    // Weight-suffixed alias — a bare 'Inter' silently falls back to the
    // browser's default serif on web (see DESIGN.md).
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    lineHeight: 14,
    textAlign: 'center',
  },
});

export default function AppLayout() {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const insets = useSafeAreaInsets();
  const webBottomInset = Platform.OS === 'web' ? insets.bottom : 0;
  const persistState = useSelector((state: any) => state._persist);
  const { user, isAuthenticated, isGuest } = useSelector((state: RootState) => state.auth);

  // Auto-sync when user authenticates
  useEffect(() => {
    const shouldSync =
      persistState?.rehydrated && // Wait for rehydration
      isAuthenticated &&
      !isGuest &&
      user?.uid &&
      !hasInitialSynced; // Only sync once per session

    if (shouldSync) {
      console.log('🔄 Auto-syncing data for authenticated user...');
      hasInitialSynced = true;

      // Resolve household pairing FIRST so the parallel syncs below route
      // through households/{hid}/... when applicable. The sync thunks read
      // householdId from state at execution time.
      // Captured so the pantry-learning load below can read from the same
      // scope; `.finally` cannot see the `.then` argument.
      let resolvedHouseholdId: string | null = null;

      dispatch(loadHouseholdId(user.uid))
        .unwrap()
        .then((householdId) => {
          resolvedHouseholdId = householdId ?? null;
          console.log(`✅ Household resolved: ${householdId ? `paired (${householdId})` : 'solo'}`);
        })
        .catch((error) => {
          console.warn('Household lookup failed; continuing in solo mode:', error);
        })
        .finally(() => {
          // Pull what the user has taught the pantry matcher. Runs after the
          // household is resolved so it reads from the shared scope when paired.
          dispatch(loadPantryLearning({ userId: user.uid, householdId: resolvedHouseholdId }))
            .unwrap()
            .catch((error) => {
              // The matcher works without its lessons, just less well.
              console.warn('Pantry learning unavailable; using rules only:', error);
            });

          // Sync inventory
          dispatch(fullSync(user.uid))
            .unwrap()
            .then(() => {
              console.log('✅ Inventory sync completed');
            })
            .catch((error) => {
              console.error('❌ Inventory sync failed:', error);
            });

          // Sync meal plan (runs in parallel). After the download resolves,
          // run a second rehydration pass: by now loadHouseholdId has resolved,
          // so any recipe the initial pass missed because the active household
          // wasn't ready yet can now be found in the correct Firestore scope.
          dispatch(downloadMealPlanFromCloud(user.uid))
            .unwrap()
            .then(async (payload) => {
              console.log('✅ Meal plan sync completed');

              // Adopt weeks the cloud has never seen. Onboarding builds the
              // first plan while the cook is still a guest, so the sync
              // middleware drops it for want of a uid and nothing carried it
              // up when the account appeared. Only weeks absent from the cloud
              // snapshot go, so this can add but never overwrite.
              //
              // Wait for the slice's own disk copy first. `shouldSync` gates on
              // the ROOT persist, but mealPlan is a nested IndexedDB persist
              // that rehydrates on its own schedule — read it too early and
              // `weeks` is still {}, so there is nothing to adopt and the
              // guest's plan is silently abandoned. That is the exact bug this
              // block exists to fix, so it has to outlast the race.
              // `payload.weeks` is the union of cloud and local, so it can
              // never say what the cloud is missing. `cloudWeekStarts` is the
              // cloud's own key set.
              const inCloud = new Set(payload?.cloudWeekStarts ?? []);
              await whenLocalMealPlanReady();
              const localWeeks = store.getState().mealPlan.weeks;
              const unseen = Object.keys(localWeeks).filter(
                (weekStart) =>
                  localWeeks[weekStart].length > 0 && !inCloud.has(weekStart)
              );
              if (unseen.length > 0) {
                console.log(`⬆️ Adopting ${unseen.length} local-only week(s) into the cloud:`, unseen);
                dispatch(pushAllWeeksToCloud({ userId: user.uid, onlyWeeks: unseen }))
                  .unwrap()
                  .catch((error) => {
                    // The plan is still on the device; the next edit re-syncs it.
                    console.warn('Could not adopt local-only weeks:', error);
                  });
              }

              return dispatch(rehydrateMissingRecipes(user.uid)).unwrap();
            })
            .catch((error) => {
              console.error('❌ Meal plan sync failed:', error);
            });

          // Sync grocery list (runs in parallel)
          dispatch(downloadGroceryListFromCloud(user.uid))
            .unwrap()
            .then(() => {
              console.log('✅ Grocery list sync completed');
            })
            .catch((error) => {
              console.error('❌ Grocery list sync failed:', error);
            });

          // Sync taste profile & feedback events (runs in parallel)
          dispatch(downloadFeedbackFromCloud(user.uid))
            .unwrap()
            .then(() => {
              console.log('✅ Feedback & taste profile sync completed');
            })
            .catch((error) => {
              console.error('❌ Feedback sync failed:', error);
            });

          // Sync recipe metadata (ratings, cooked stamps, notes, favorites)
          dispatch(downloadRecipeMetadataFromCloud(user.uid))
            .unwrap()
            .then(() => {
              console.log('✅ Recipe metadata sync completed');
            })
            .catch((error) => {
              console.error('❌ Recipe metadata sync failed:', error);
            });

          // Sync standalone cook-log entries ("I cooked this" from recipe detail)
          dispatch(downloadCookLogFromCloud(user.uid))
            .unwrap()
            .then(() => {
              console.log('✅ Cook log sync completed');
            })
            .catch((error) => {
              console.error('❌ Cook log sync failed:', error);
            });

          // Sync user preferences (dietary dislikes, learning, schedule)
          dispatch(downloadUserPreferencesFromCloud(user.uid))
            .unwrap()
            .then(() => {
              console.log('✅ User preferences sync completed');
            })
            .catch((error) => {
              console.error('❌ User preferences sync failed:', error);
            });
        });
    }
  }, [persistState?.rehydrated, isAuthenticated, isGuest, user?.uid, dispatch]);

  // Reactive grocery list sync - auto-regenerates when meal plan changes
  useGroceryListSync();

  // Live Firestore subscriptions for shared surfaces (grocery list, meal
  // plan) so partner edits appear in real time. No-op when not signed in.
  useHouseholdLiveSync();

  // Reset sync flag when user logs out
  useEffect(() => {
    if (isGuest || !isAuthenticated) {
      hasInitialSynced = false;
    }
  }, [isGuest, isAuthenticated]);

  // Flush any pending debounced writes when the tab loses visibility.
  //
  // Two separate buffers need draining: the meal plan's Firestore sync debounce,
  // and redux-persist's own write throttle. Without this, a user who closes the
  // tab within a second of their last change loses that window — the other
  // device sees nothing, and local storage is a step behind on next load.
  //
  // Not gated on auth: persisted local state matters for guests too.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (isAuthenticated && user?.uid) {
          flushPendingMealPlanSync(() => store.getState());
        }
        persistor.flush();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, user?.uid]);

  // Wait for Redux Persist to rehydrate
  if (!persistState?.rehydrated) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </View>
    );
  }

  const desktop = isDesktopWeb();

  const tabs = (
    <NavigationRestorer userId={user?.uid}>
      <Tabs
        // Where back goes from a recipe, a day, the settings — see tabConfig.ts.
        backBehavior={TAB_BACK_BEHAVIOR}
        screenOptions={{
            headerShown: false,
            tabBarActiveTintColor: Paprika.accent,
            tabBarInactiveTintColor: Paprika.textSecondary,
            tabBarStyle: desktop
              ? { display: 'none' }
              : {
                  backgroundColor: Paprika.background,
                  borderTopWidth: 1,
                  borderTopColor: Paprika.borderLight,
                  paddingTop: 6,
                  paddingBottom: Platform.OS === 'ios' ? 24 : Platform.OS === 'web' ? 16 + webBottomInset : 12,
                  height: Platform.OS === 'ios' ? 84 : Platform.OS === 'web' ? 76 + webBottomInset : 70,
                },
            // Two lines allowed so a long label (FR "Garde-manger") wraps
            // instead of being clipped mid-word on narrow phones (#531).
            tabBarLabel: ({ children, color }) => (
              <Text numberOfLines={2} style={[styles.tabLabel, { color }]}>
                {children}
              </Text>
            ),
            tabBarLabelStyle: styles.tabLabel,
          }}
        >
          {/* Main tabs — see src/navigation/tabConfig.ts for the 4-tab ceiling */}
          {VISIBLE_TABS.map(({ name, labelKey, icon }) => (
            <Tabs.Screen
              key={name}
              name={name}
              options={{
                title: t(labelKey),
                tabBarIcon: ({ color }) => (
                  <Feather name={icon} size={22} color={color} />
                ),
              }}
            />
          ))}
          {/* Hidden screens (reachable by navigation, absent from the tab bar).
              `settings` joined this list in #531 — it lives in the header avatar. */}
          {HIDDEN_ROUTES.map((name) => (
            <Tabs.Screen key={name} name={name} options={{ href: null, title: '' }} />
          ))}
        </Tabs>
    </NavigationRestorer>
  );

  if (desktop) {
    return (
      <View style={{ flex: 1, flexDirection: 'row' }}>
        <DesktopSidebar />
        <View style={{ flex: 1 }}>{tabs}</View>
        <GroceryToast />
        <ChefToast />
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      {tabs}
      <GroceryToast />
      <ChefToast />
    </View>
  );
}
